import { MaterialCommunityIcons } from '@expo/vector-icons';
import { StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import { iconSize, spacing, useAppTheme } from '@/theme';

interface SettingsNavigationItemProps {
  readonly title: string;
  readonly description?: string;
  readonly onPress: () => void;
  readonly opensExternally?: boolean;
  readonly testID?: string;
}

export const SettingsNavigationItem = ({
  title,
  description,
  onPress,
  opensExternally = false,
  testID,
}: SettingsNavigationItemProps) => {
  const { colors } = useAppTheme();
  const accessibilityLabel = description ? `${title}, ${description}` : title;
  const accessibilityHint = opensExternally ? 'Opens in browser' : 'Navigates to settings detail';

  return (
    <TouchableRipple
      accessibilityRole="button"
      accessibilityHint={accessibilityHint}
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      style={styles.pressable}
      testID={testID}>
      <View style={styles.content}>
        <View style={styles.textContainer}>
          <Text numberOfLines={1} style={[styles.title, { color: colors.textNdPrimary }]}>
            {title}
          </Text>
          {description ? (
            <Text style={[styles.description, { color: colors.textNdSecondary }]}>
              {description}
            </Text>
          ) : null}
        </View>
        <View style={styles.trailingElement}>
          {opensExternally ? (
            <MaterialCommunityIcons
              color={colors.iconNdPrimary}
              name="open-in-new"
              size={iconSize.md}
              testID="settings-external-link-icon"
            />
          ) : (
            <Text style={[styles.chevron, { color: colors.iconNdPrimary }]}>›</Text>
          )}
        </View>
      </View>
    </TouchableRipple>
  );
};

const styles = StyleSheet.create({
  pressable: {
    borderRadius: 8,
    minHeight: 48,
    overflow: 'hidden',
  },
  content: {
    minHeight: 48,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing[2],
    paddingVertical: spacing[2],
  },
  textContainer: {
    flex: 1,
    gap: 3,
    justifyContent: 'center',
  },
  title: {
    fontSize: 16,
    lineHeight: 24,
    letterSpacing: 0.5,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0.25,
  },
  trailingElement: {
    alignItems: 'center',
    height: 40,
    justifyContent: 'center',
    width: 24,
  },
  chevron: {
    fontSize: 24,
    lineHeight: 24,
  },
});
